/**
 * Cálculo central de stats de equipamento. Fonte única para: dano/armadura base de um
 * item (raridade + level), soma de rolls, DPS para a UI, e o LOADOUT efetivo
 * (mods agregados + arma efetiva + traits de maestria). Evita duplicar fórmulas.
 */
import { GameConfig } from "../config/GameConfig";
import { definitionById, isWeaponDef } from "./Catalog";
import { rarityMult } from "./Rarity";
import { emptyMods } from "./EquipmentTypes";
import type { EffectiveWeapon, EquipmentInstance, StatMods, WeaponType } from "./EquipmentTypes";

const eq = GameConfig.equipment;

/** Dano base de uma ARMA (definição × raridade × nível de upgrade). */
export function weaponBaseDamage(inst: EquipmentInstance): number {
  const def = definitionById(inst.definitionId);
  if (!def || !isWeaponDef(def)) return 0;
  return Math.round(def.baseDamage * rarityMult(inst.rarity) * (1 + eq.upgradeDamagePerLevel * (inst.level - 1)));
}

export function armorBaseOf(inst: EquipmentInstance): { armor: number; hp: number } {
  const def = definitionById(inst.definitionId);
  if (!def || isWeaponDef(def)) return { armor: 0, hp: 0 };
  const scale = rarityMult(inst.rarity) * (1 + eq.upgradeArmorPerLevel * (inst.level - 1));
  return { armor: Math.round(def.baseArmor * scale), hp: Math.round(def.baseHp * scale) };
}

/** 10.9L23 — valor EFETIVO de um roll: escala com o nível de forja do item, igual ao dano/armadura
 *  base — antes só a linha "Dano" no topo do card crescia forjando; os atributos secundários
 *  (crítico, vel. ataque, dano por elemento…) ficavam travados no valor do drop pra sempre. */
export function effectiveRollValue(inst: EquipmentInstance, value: number): number {
  return value * (1 + eq.upgradeRollPerLevel * (inst.level - 1));
}

/** Acumula os rolls de um item nos mods (valor já escalado pelo nível de forja). */
function addRolls(inst: EquipmentInstance, mods: StatMods): void {
  for (const r of inst.rolls) {
    const v = effectiveRollValue(inst, r.value);
    switch (r.key) {
      case "damage": mods.damage += v; break;
      case "attackSpeed": mods.attackSpeedMult += v; break;
      case "critChance": mods.critChance += v; break;
      case "critDamage": mods.critDamageMult += v; break;
      case "maxHp": mods.maxHp += v; break;
      case "armor": mods.armor += v; break;
      case "moveSpeed": mods.moveSpeedMult += v; break; // rolls de moveSpeed são %
      case "attackRange": mods.attackRange += v; break;
      // 10.5T — atributos novos vindos do EQUIPAMENTO
      case "dmgSlash": mods.dmgSlashPct += v; break;
      case "dmgPierce": mods.dmgPiercePct += v; break;
      case "dmgBlunt": mods.dmgBluntPct += v; break;
      case "defSlash": mods.defSlashPct += v; break;
      case "defPierce": mods.defPiercePct += v; break;
      case "defBlunt": mods.defBluntPct += v; break;
      case "block": mods.blockChance += v; break;
      case "regen": mods.regenPct += v; break;
      case "lifesteal": mods.lifeStealPct += v; break; // 10.9EQ
      case "maxHpPct": mods.maxHpMult += v; break; // 10.9FR — % de vida
      case "damagePct": mods.damageMult += v; break; // 10.9FR — % de dano
    }
  }
}

/** DPS aproximado de uma ARMA (para comparação na UI). Centralizado — não duplicar. */
export function weaponDps(inst: EquipmentInstance): number {
  const def = definitionById(inst.definitionId);
  if (!def || !isWeaponDef(def)) return 0;
  let dmg = weaponBaseDamage(inst);
  let aps = def.attacksPerSecond;
  let crit = GameConfig.player.critChance;
  const critMult = GameConfig.player.critDamageMult;
  for (const r of inst.rolls) {
    const v = effectiveRollValue(inst, r.value);
    if (r.key === "damage") dmg += v;
    else if (r.key === "attackSpeed") aps *= 1 + v;
    else if (r.key === "critChance") crit += v;
  }
  const critExp = 1 + crit * (critMult - 1);
  return Math.round(dmg * aps * critExp);
}

export interface Loadout {
  mods: StatMods;
  weapon: EffectiveWeapon | null; // null => desarmado (o Player usa o fallback)
  traits: Set<string>;
  /** 10.9EU — golpes/s da arma ANTES de qualquer bonus (o Game reaplica com classe+arvore+treino+buff). */
  baseAps: number;
}

/** Bônus numéricos + traits da maestria do arquétipo equipado no nível atual. */
function applyMastery(weaponType: WeaponType, level: number, mods: StatMods, traits: Set<string>): number {
  let projSpeedMult = 0;
  const table = GameConfig.mastery.bonuses[weaponType] as Record<string, Record<string, number | string>>;
  for (const key of Object.keys(table)) {
    if (level < Number(key)) continue;
    const bonus = table[key];
    for (const bk of Object.keys(bonus)) {
      const v = bonus[bk];
      if (bk === "trait" && typeof v === "string") traits.add(v);
      else if (typeof v === "number") {
        if (bk === "attackSpeedMult") mods.attackSpeedMult += v;
        else if (bk === "damageMult") mods.damageMult += v;
        else if (bk === "critChance") mods.critChance += v;
        else if (bk === "critDamageMult") mods.critDamageMult += v;
        else if (bk === "attackRange") mods.attackRange += v;
        else if (bk === "projectileSpeedMult") projSpeedMult += v;
      }
    }
  }
  // 10.9L22 — maestria sem teto: acima do último marco, cada nível soma um pouco mais de dano.
  const { pastCapLevel: cap, pastCapDamagePerLevel: perLevel } = GameConfig.mastery;
  if (level > cap) mods.damageMult += (level - cap) * perLevel;
  return projSpeedMult;
}

/**
 * Calcula o loadout efetivo a partir das instâncias equipadas + níveis de maestria.
 * `equipped` = itens equipados (weapon/head/chest/accessory); `masteryLevel(type)` fornece o nível.
 */
export function computeLoadout(
  equipped: (EquipmentInstance | null)[],
  masteryLevel: (t: WeaponType) => number,
): Loadout {
  const mods = emptyMods();
  const traits = new Set<string>();
  let weapon: EffectiveWeapon | null = null;
  let projSpeedMult = 0;

  for (const inst of equipped) {
    if (!inst) continue;
    const def = definitionById(inst.definitionId);
    if (!def) continue;
    if (isWeaponDef(def)) {
      mods.damage += weaponBaseDamage(inst);
      addRolls(inst, mods);
      projSpeedMult += applyMastery(def.weaponType, masteryLevel(def.weaponType), mods, traits);
      weapon = {
        weaponType: def.weaponType,
        attackType: def.attackType,
        damageType: def.damageType,
        attackRange: def.attackRange,
        attacksPerSecond: def.attacksPerSecond,
        arc: def.arc,
        knockback: def.knockback,
        projectileSpeed: def.projectileSpeed,
        visualId: def.visualId,
      };
    } else {
      const b = armorBaseOf(inst);
      mods.armor += b.armor;
      mods.maxHp += b.hp;
      addRolls(inst, mods);
    }
  }

  // aplica mods dependentes da arma na EffectiveWeapon
  const baseAps = weapon ? weapon.attacksPerSecond : 0; // 10.9EU — o Game reaplica com TODOS os bonus
  if (weapon) {
    // 10.9S — TETO de velocidade (senão os nós infinitos de atkspd viravam metralhadora)
    weapon.attacksPerSecond = Math.min(GameConfig.combat.maxAttacksPerSecond, weapon.attacksPerSecond * (1 + mods.attackSpeedMult));
    weapon.attackRange = weapon.attackRange + mods.attackRange;
    weapon.projectileSpeed = weapon.projectileSpeed * (1 + projSpeedMult);
  }

  return { mods, weapon, traits, baseAps };
}
